/* -------------------------- Design imports start -------------------------- */
import { Grid, Typography } from '@mui/material'

/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import MultilevelDrawer from '../../../components/layout/MultilevelDrawer'
import { missingRequiredFormFields } from '../../../utils/functions'
import GetConstant from '../../Customer/utils/constants'
import { Form, type FormFieldType, type FormObject } from "../../Form";
import { type Address } from "../../../utils/types";
import { type ReactElement } from "react";
/* ------------------------- Functional imports end ------------------------- */

interface Props {
  open: boolean
  setOpen: (open: boolean) => void
  formState: {
    state: Address | undefined
    setState: React.Dispatch<React.SetStateAction<Address | undefined>>
  }
  onClose?: () => void
  onConfirm?: (input: Address) => Promise<'SUCCESS' | 'ERROR'>
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */

export default function AddressDrawer(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const { open, formState, setOpen, onClose = () => null, onConfirm = (input: Address): null => null } = props

  const { t } = useTranslation()
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Data states start --------------------------- */

  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------- Utility functions start ------------------------ */
  // some API functions like fetch, create, ...
  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------ Callback functions start ------------------------ */
  const handleClose = (preventOnClose = false): void => {
    !preventOnClose && onClose()
  }

  const handleConfirm = async (): Promise<void> => {
    if (!formState.state) {
      throw new Error('Address formState.state is undefined')
    }
    // posting request to server and waiting on response
    const confirmStatus = await onConfirm(formState.state)

    // visual feedback: handle server response status
    if (confirmStatus === 'SUCCESS') {
      toast.success(t('address:feedback.success.creatingAddress'))
    } else {
      toast.error(t('address:feedback.error.creatingAddress'))
    }
    handleClose(true)
  }
  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  const addressFormFields = GetConstant({ name: 'addressFormFields' }) as FormFieldType[]
  /* -------------------------- Render constants end -------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */

  if (!formState.state) {
    return <></>
  }

  return (
    <MultilevelDrawer
      open={open}
      setOpen={setOpen}
      size="big"
      title={t('address:content.heading.createNewAddress') }
      onClose={handleClose}
      confirmButtonProps={{
        text: t('common:interaction.button.add'),
        onConfirm: () => void (async () => await handleConfirm())(),
        disabled: missingRequiredFormFields(addressFormFields, formState.state),
      }}
    >
      <Typography sx={{ mb: 2, mt: 4 }} variant="h6">
        {t('common:content.label.address')}
      </Typography>
      <Grid container>
        <Form
          editing={true}
          formFields={addressFormFields}
          formObject={formState.state}
          onChange={(value: FormObject) => formState.setState(value as Address)}
        />
      </Grid>
    </MultilevelDrawer>
  )
}
